import { readCitations } from "./citations";
/**
 * Is this finished answer actually finished? (`T-57`)
 *
 * ── THE PROBLEM, RESTATED, BECAUSE IT IS THE MOST DANGEROUS ONE HERE ────────
 * A Durable Object that dies mid-turn — a deploy landing mid-answer, an
 * eviction, an out-of-memory reset — does **not** re-invoke the model. The next
 * constructor finds the stream row still marked `streaming`, adopts it, offers
 * it to the reconnecting client, replays every chunk that was buffered before
 * the object died, and sends `done: true`. No error frame. No `error: true`.
 * The partial is then persisted as the assistant's message.
 *
 * `T43` measured that, in workerd, deliberately: the last test in
 * `apps/ai/src/__tests__/pool/resumable-streaming.test.ts` kills the object with
 * `abortAllDurableObjects()`.
 *
 * ── WHY THAT IS WORSE HERE THAN IN AN ORDINARY CHAT APP ─────────────────────
 * This app answers training questions, and `T51` was told that caveats precede
 * recommendations. A sentence severed mid-caveat — "do X, unless you…" — is not
 * a degraded answer, it is an inverted one, and the person reading it is about
 * to load a bar. An answer that stops is fine; an answer that stops *and looks
 * complete* is the failure.
 *
 * ── WHAT THIS FILE READS NOW, AND WHY IT CHANGED (D77) ──────────────────────
 * It reads ONE CHARACTER: the end-of-answer marker the server appends to the
 * validated text, in `apps/ai/src/agents/trainer-agent.ts`, where the model's
 * output ends. Present means the answer got here whole. Absent means it did
 * not, and `TrainerSocket` then discards the answer and offers the user's own
 * message back with a resend button (`D77`) — the behaviour ChatGPT, Claude and
 * Perplexity have, and better than a warning under a suspect answer.
 *
 * ── WHY THAT IS STRONGER THAN THE `done: true` ALREADY ON THE WIRE ──────────
 * Because the flag is FABRICATED by the object that adopted the dead stream: it
 * describes the STREAM, not the ANSWER. The marker is content, so it was either
 * buffered before the death or it does not exist, and no resume path can invent
 * it after the fact. Absence is signal rather than noise. The server also
 * strips the character from the model's own output before appending its own, so
 * neither a model echoing its persisted transcript nor a user who simply asks
 * for the character can forge a completion certificate for half an answer.
 *
 * ── WHAT THIS REPLACED, AND WHY THE CITATION CHECK IS STILL BELOW ───────────
 * Until `D47`'s 2026-08-27 amendment the tail was structural for a different
 * reason: the answer was not the model's prose, it was
 * `validateTrainerAnswer`'s RENDERING of it, and that rendering ended in a
 * mandatory sources block. That amendment turned citation rendering off behind
 * `CITATION_RENDERING_ENABLED`, so `readCitations` returns `[]` on every real
 * answer and that whole branch is DORMANT — which is exactly why the amendment
 * was made conditional on this task existing.
 *
 * The branch is kept rather than deleted, and it is kept as a check that runs
 * AFTER the marker rather than instead of it: with the flag back on, an answer
 * carrying inline expansions and no block is still a truncated answer, and it
 * costs nothing to keep saying so. `D47`'s amendment also requires the citation
 * code to stay reachable — `pnpm deadcode` is a CI gate and `citations.ts` has
 * no other caller in the app.
 *
 * ── WHY THERE IS A SECOND COPY OF THE MARKER, WHICH IS A HAZARD ─────────────
 * `ANSWER_END_MARKER` below is the same character as the one exported by
 * `apps/ai/src/agents/trainer-agent.ts`, declared twice with nothing comparing
 * the two. That is the shape `T53` removed from the WebSocket close codes by
 * moving them into `@repo/contract/iam`, and it is the right home for this one
 * too; the change could not touch that package. If they ever drift, the symptom is
 * not an error — it is every answer being discarded as truncated, forever.
 *
 * ── THE ASYMMETRY IS DELIBERATE ─────────────────────────────────────────────
 * A false "may be cut off" costs the user a tap. A false "complete" is a
 * severed caveat acted on in a gym. Every ambiguous case therefore resolves to
 * truncated. `D77` changed the price of the first half — a discarded answer and
 * a tap, rather than a line of text under an answer that was fine — and it is
 * still much the cheaper side.
 */
/**
 * U+241E SYMBOL FOR RECORD SEPARATOR — the last character of a whole answer.
 *
 * One code point, so the server's chunker can neither split it across two
 * deltas nor across two rows of its buffer and no client ever holds half of it.
 * Not whitespace, so `trim()` cannot eat it.
 */
export const ANSWER_END_MARKER = "␞";
const ANSWER_END_MARKERS = /␞/g;
/**
 * The answer with every marker removed — what is displayed, measured, keyed on
 * and sent back as context. Nothing outside this module may hold the raw text
 * for longer than it takes to assess it.
 */
export function stripEndMarker(text) {
    return text.replace(ANSWER_END_MARKERS, "");
}
/** A marker with more of the answer after it — a SEAM, not the end. */
const ANSWER_SEAMS = /␞(?=[\s\S]*\S)/g;
/**
 * A stored answer that ran across continuations, as paragraphs (`D191`).
 *
 * A continuation — the turn an `askUser` answer wakes — is persisted in the
 * SAME assistant message as the turn that asked, so the restored text is two
 * or more answers each ending in the marker. Stripped with
 * {@link stripEndMarker} they ran together mid-sentence — *"preciso da sua
 * resposta:Adicionei…"* — which is how a question asked with no prose now
 * always restores. Each seam becomes a paragraph break; the final marker is
 * removed as before.
 */
/**
 * The answers a stored message holds, in order (`D191`): one per end marker —
 * the turn, then each continuation an `askUser` answer woke. The card
 * interleaves them with the questions so a reply sits under the question it
 * answers rather than above it.
 */
export function answerSegments(text) {
    return text
        .split(ANSWER_END_MARKERS)
        .map((segment) => segment.trim())
        .filter((segment) => segment !== "");
}
export function joinAnswerSegments(text) {
    return stripEndMarker(text.replace(ANSWER_SEAMS, "\n\n"));
}
/**
 * Judge a COMPLETED answer, from the RAW text — markers included. Never call
 * this on a partial one, and never on text that has already been stripped: a
 * stripped answer is indistinguishable from a truncated one, which is the whole
 * point of the character.
 */
export function assessAnswer(raw) {
    const answer = stripEndMarker(raw).trim();
    if (answer === "") {
        // Before the marker check, deliberately. A stream that produced a marker
        // and no prose ended, but it did not answer, and "empty" is what says so.
        return { complete: false, reason: "empty" };
    }
    if (!raw.includes(ANSWER_END_MARKER)) {
        return { complete: false, reason: "end-marker-missing" };
    }
    // ── DORMANT WHILE CITATION RENDERING IS OFF (`D47` amended) ─────────────
    // `readCitations` returns `[]` for every answer the server currently sends,
    // so this is a no-op today. With the flag on it is a second, independent
    // witness: an answer that cites inline and carries no block did not finish
    // rendering, whatever the marker says.
    const citations = readCitations(answer);
    if (citations.length === 0) {
        return { complete: true };
    }
    // The block is identified by having produced at least one parsed detail;
    // `readCitations` fills `population` and `evidenceLevel` only from it.
    const withDetail = citations.filter((citation) => citation.evidenceLevel !== null);
    if (withDetail.length === 0) {
        return { complete: false, reason: "sources-block-missing" };
    }
    if (withDetail.length < citations.length) {
        // The block began and stopped part-way. Every ordinal cited inline gets an
        // entry, so a short block is a cut-off one — not a server that decided some
        // references did not need listing.
        return { complete: false, reason: "sources-block-incomplete" };
    }
    return { complete: true };
}
//# sourceMappingURL=answer-integrity.js.map